import {
  ProductRealmSchema
} from "./chunk-HJPR3SS7.js";

// organization/organization.schema.ts
import { z } from "zod";
var ORGANIZATION_TYPES = ["buyer", "supplier", "both"];
var OrganizationTypeSchema = z.enum(ORGANIZATION_TYPES);
var MEMBER_ROLES = ["admin", "member", "sales_rep"];
var MemberRoleSchema = z.enum(MEMBER_ROLES);
var SCOPE_MODES = ["all", "restricted"];
var ScopeModeSchema = z.enum(SCOPE_MODES);
var LOCATION_TYPES = ["hq", "retail_store", "warehouse", "office", "dispensary", "storefront"];
var LocationTypeSchema = z.enum(LOCATION_TYPES);
var FULFILLMENT_METHODS = ["local_delivery", "mail_carrier"];
var FulfillmentMethodSchema = z.enum(FULFILLMENT_METHODS);
var ACCOUNT_TYPES = ["checking", "savings"];
var AccountTypeSchema = z.enum(ACCOUNT_TYPES);
var DbOrganizationSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  legal_entity_name: z.string().nullable(),
  slug: z.string().nullable(),
  type: OrganizationTypeSchema,
  contact_email: z.string().nullable(),
  contact_phone: z.string().nullable(),
  website: z.string().nullable(),
  tax_id: z.string().nullable(),
  is_active: z.boolean(),
  skip_fulfillment_offers: z.boolean(),
  fulfillment_method: FulfillmentMethodSchema.nullable(),
  default_spread_rate: z.string().nullable(),
  requires_metrc_compliance: z.boolean().nullable(),
  transporter_license_id: z.string().uuid().nullable(),
  metadata: z.record(z.unknown()).nullable(),
  created_at: z.union([z.string(), z.date()]),
  updated_at: z.union([z.string(), z.date()]),
  deleted_at: z.union([z.string(), z.date()]).nullable()
});
var CreateDbOrganizationSchema = z.object({
  id: z.string().uuid().optional(),
  name: z.string(),
  legal_entity_name: z.string().nullable().optional(),
  slug: z.string().nullable().optional(),
  type: OrganizationTypeSchema.optional(),
  contact_email: z.string().nullable().optional(),
  contact_phone: z.string().nullable().optional(),
  website: z.string().nullable().optional(),
  tax_id: z.string().nullable().optional(),
  is_active: z.boolean().optional(),
  skip_fulfillment_offers: z.boolean().optional(),
  fulfillment_method: FulfillmentMethodSchema.nullable().optional(),
  default_spread_rate: z.string().nullable().optional(),
  requires_metrc_compliance: z.boolean().nullable().optional(),
  transporter_license_id: z.string().uuid().nullable().optional(),
  metadata: z.record(z.unknown()).nullable().optional(),
  created_at: z.string().optional(),
  updated_at: z.string().optional()
});
var UpdateDbOrganizationSchema = CreateDbOrganizationSchema.partial();
var OrganizationSchema = z.object({
  id: z.string().uuid(),
  name: z.string().min(1),
  legalEntityName: z.string().nullable(),
  slug: z.string().nullable(),
  type: OrganizationTypeSchema,
  contactEmail: z.string().email().nullable(),
  contactPhone: z.string().nullable(),
  website: z.string().nullable(),
  taxId: z.string().nullable(),
  isActive: z.boolean(),
  skipFulfillmentOffers: z.boolean(),
  /** @deprecated Derived from brand realm + org_brands overrides */
  fulfillmentMethod: FulfillmentMethodSchema.nullable(),
  defaultSpreadRate: z.number().nullable(),
  /** @deprecated Derived from brand realm */
  requiresMetrcCompliance: z.boolean().nullable(),
  transporterLicenseId: z.string().uuid().nullable(),
  metadata: z.record(z.unknown()).nullable(),
  createdAt: z.date(),
  updatedAt: z.date(),
  deletedAt: z.date().nullable()
});
var OrganizationDtoSchema = z.object({
  id: z.string(),
  name: z.string(),
  legalEntityName: z.string().nullable(),
  slug: z.string().nullable(),
  type: OrganizationTypeSchema,
  contactEmail: z.string().nullable(),
  contactPhone: z.string().nullable(),
  website: z.string().nullable(),
  taxId: z.string().nullable(),
  isActive: z.boolean(),
  skipFulfillmentOffers: z.boolean(),
  /** @deprecated Derived from brand realm + org_brands overrides */
  fulfillmentMethod: FulfillmentMethodSchema.nullable(),
  defaultSpreadRate: z.number().nullable(),
  /** @deprecated Derived from brand realm */
  requiresMetrcCompliance: z.boolean().nullable(),
  transporterLicenseId: z.string().uuid().nullable(),
  metadata: z.record(z.unknown()).nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime()
});
var CreateOrganizationDtoSchema = z.object({
  name: z.string().min(1),
  legalEntityName: z.string().nullable().optional(),
  slug: z.string().nullable().optional(),
  type: OrganizationTypeSchema.optional(),
  contactEmail: z.string().email().nullable().optional(),
  contactPhone: z.string().nullable().optional(),
  website: z.string().url().nullable().optional(),
  taxId: z.string().nullable().optional(),
  skipFulfillmentOffers: z.boolean().optional(),
  defaultSpreadRate: z.number().nullable().optional(),
  transporterLicenseId: z.string().uuid().nullable().optional(),
  metadata: z.record(z.unknown()).nullable().optional()
});
var UpdateOrganizationDtoSchema = CreateOrganizationDtoSchema.partial();
var DbOrganizationMemberSchema = z.object({
  id: z.string().uuid(),
  organization_id: z.string().uuid(),
  customer_id: z.string().uuid().nullable(),
  first_name: z.string(),
  last_name: z.string(),
  email: z.string(),
  phone: z.string().nullable(),
  portal_user_id: z.string().uuid().nullable(),
  role: MemberRoleSchema,
  brand_scope_mode: ScopeModeSchema,
  location_scope_mode: ScopeModeSchema,
  is_active: z.boolean(),
  invited_by: z.string().uuid().nullable(),
  joined_at: z.union([z.string(), z.date()]).nullable(),
  metrc_employee_id: z.string().nullable(),
  drivers_license_number: z.string().nullable(),
  metadata: z.record(z.unknown()).nullable(),
  created_at: z.union([z.string(), z.date()]),
  updated_at: z.union([z.string(), z.date()])
});
var CreateDbOrganizationMemberSchema = z.object({
  id: z.string().uuid().optional(),
  organization_id: z.string().uuid(),
  customer_id: z.string().uuid().nullable().optional(),
  first_name: z.string(),
  last_name: z.string(),
  email: z.string(),
  phone: z.string().nullable().optional(),
  portal_user_id: z.string().uuid().nullable().optional(),
  role: MemberRoleSchema.optional(),
  brand_scope_mode: ScopeModeSchema.optional(),
  location_scope_mode: ScopeModeSchema.optional(),
  is_active: z.boolean().optional(),
  invited_by: z.string().uuid().nullable().optional(),
  metrc_employee_id: z.string().nullable().optional(),
  drivers_license_number: z.string().nullable().optional(),
  metadata: z.record(z.unknown()).nullable().optional(),
  created_at: z.string().optional(),
  updated_at: z.string().optional()
});
var OrganizationMemberSchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string().uuid(),
  customerId: z.string().uuid().nullable(),
  firstName: z.string(),
  lastName: z.string(),
  email: z.string(),
  phone: z.string().nullable(),
  portalUserId: z.string().uuid().nullable(),
  role: MemberRoleSchema,
  brandScopeMode: ScopeModeSchema,
  locationScopeMode: ScopeModeSchema,
  isActive: z.boolean(),
  invitedBy: z.string().uuid().nullable(),
  joinedAt: z.date().nullable(),
  metrcEmployeeId: z.string().nullable(),
  driversLicenseNumber: z.string().nullable(),
  metadata: z.record(z.unknown()).nullable(),
  createdAt: z.date(),
  updatedAt: z.date()
});
var OrganizationMemberDtoSchema = z.object({
  id: z.string(),
  organizationId: z.string(),
  customerId: z.string().nullable(),
  firstName: z.string(),
  lastName: z.string(),
  email: z.string(),
  phone: z.string().nullable(),
  portalUserId: z.string().nullable(),
  role: MemberRoleSchema,
  brandScopeMode: ScopeModeSchema,
  locationScopeMode: ScopeModeSchema,
  isActive: z.boolean(),
  invitedBy: z.string().nullable(),
  joinedAt: z.string().datetime().nullable(),
  metrcEmployeeId: z.string().nullable(),
  driversLicenseNumber: z.string().nullable(),
  metadata: z.record(z.unknown()).nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime()
});
var AddMemberDtoSchema = z.object({
  firstName: z.string().min(1),
  lastName: z.string().min(1),
  email: z.string().email(),
  phone: z.string().nullable().optional(),
  customerId: z.string().uuid().optional(),
  role: MemberRoleSchema.optional()
});
var UpdateMemberDtoSchema = z.object({
  firstName: z.string().min(1).optional(),
  lastName: z.string().min(1).optional(),
  email: z.string().email().optional(),
  phone: z.string().nullable().optional(),
  role: MemberRoleSchema.optional(),
  brandScopeMode: ScopeModeSchema.optional(),
  locationScopeMode: ScopeModeSchema.optional()
});
var UpdateMemberRoleDtoSchema = z.object({
  role: MemberRoleSchema
});
var OnboardPortalRoleSchema = z.enum(["supplier_admin", "supplier_member", "sales_rep"]);
var OnboardMemberDtoSchema = z.object({
  firstName: z.string().min(1),
  lastName: z.string().min(1),
  email: z.string().email(),
  phone: z.string().nullable().optional(),
  role: MemberRoleSchema.optional().default("member"),
  portalRole: OnboardPortalRoleSchema.optional().default("supplier_member")
});
var OnboardMemberResponseDtoSchema = z.object({
  member: OrganizationMemberDtoSchema,
  portalUser: z.object({
    id: z.string(),
    email: z.string(),
    role: z.string()
  })
});
var DbOrganizationLocationSchema = z.object({
  id: z.string().uuid(),
  organization_id: z.string().uuid(),
  name: z.string(),
  location_type: LocationTypeSchema,
  is_hq: z.boolean(),
  address_1: z.string().nullable(),
  address_2: z.string().nullable(),
  city: z.string().nullable(),
  province: z.string().nullable(),
  postal_code: z.string().nullable(),
  country_code: z.string(),
  phone: z.string().nullable(),
  email: z.string().nullable(),
  location_lat: z.string().nullable(),
  location_lng: z.string().nullable(),
  is_active: z.boolean(),
  is_public: z.boolean(),
  stock_location_id: z.string().uuid().nullable(),
  compliance_license_id: z.string().uuid().nullable(),
  metadata: z.record(z.unknown()).nullable(),
  created_at: z.union([z.string(), z.date()]),
  updated_at: z.union([z.string(), z.date()]),
  deleted_at: z.union([z.string(), z.date()]).nullable()
});
var CreateDbOrganizationLocationSchema = z.object({
  id: z.string().uuid().optional(),
  organization_id: z.string().uuid(),
  name: z.string(),
  location_type: LocationTypeSchema.optional(),
  is_hq: z.boolean().optional(),
  address_1: z.string().nullable().optional(),
  address_2: z.string().nullable().optional(),
  city: z.string().nullable().optional(),
  province: z.string().nullable().optional(),
  postal_code: z.string().nullable().optional(),
  country_code: z.string().optional(),
  phone: z.string().nullable().optional(),
  email: z.string().nullable().optional(),
  location_lat: z.string().nullable().optional(),
  location_lng: z.string().nullable().optional(),
  is_active: z.boolean().optional(),
  is_public: z.boolean().optional(),
  stock_location_id: z.string().uuid().nullable().optional(),
  compliance_license_id: z.string().uuid().nullable().optional(),
  metadata: z.record(z.unknown()).nullable().optional(),
  created_at: z.string().optional(),
  updated_at: z.string().optional()
});
var UpdateDbOrganizationLocationSchema = CreateDbOrganizationLocationSchema.partial();
var OrganizationLocationSchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string().uuid(),
  name: z.string().min(1),
  locationType: LocationTypeSchema,
  isHq: z.boolean(),
  address1: z.string().nullable(),
  address2: z.string().nullable(),
  city: z.string().nullable(),
  province: z.string().nullable(),
  postalCode: z.string().nullable(),
  countryCode: z.string(),
  phone: z.string().nullable(),
  email: z.string().nullable(),
  locationLat: z.string().nullable(),
  locationLng: z.string().nullable(),
  isActive: z.boolean(),
  isPublic: z.boolean(),
  stockLocationId: z.string().uuid().nullable(),
  complianceLicenseId: z.string().uuid().nullable(),
  metadata: z.record(z.unknown()).nullable(),
  createdAt: z.date(),
  updatedAt: z.date(),
  deletedAt: z.date().nullable()
});
var OrganizationLocationDtoSchema = z.object({
  id: z.string(),
  organizationId: z.string(),
  name: z.string(),
  locationType: LocationTypeSchema,
  isHq: z.boolean(),
  address1: z.string().nullable(),
  address2: z.string().nullable(),
  city: z.string().nullable(),
  province: z.string().nullable(),
  postalCode: z.string().nullable(),
  countryCode: z.string(),
  phone: z.string().nullable(),
  email: z.string().nullable(),
  locationLat: z.string().nullable(),
  locationLng: z.string().nullable(),
  isActive: z.boolean(),
  isPublic: z.boolean(),
  stockLocationId: z.string().nullable(),
  complianceLicenseId: z.string().nullable(),
  metadata: z.record(z.unknown()).nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime()
});
var CreateOrganizationLocationDtoSchema = z.object({
  name: z.string().min(1),
  locationType: LocationTypeSchema.optional(),
  isHq: z.boolean().optional(),
  address1: z.string().nullable().optional(),
  address2: z.string().nullable().optional(),
  city: z.string().nullable().optional(),
  province: z.string().nullable().optional(),
  postalCode: z.string().nullable().optional(),
  countryCode: z.string().optional(),
  phone: z.string().nullable().optional(),
  email: z.string().email().nullable().optional(),
  locationLat: z.string().nullable().optional(),
  locationLng: z.string().nullable().optional(),
  isPublic: z.boolean().optional(),
  stockLocationId: z.string().uuid().nullable().optional(),
  complianceLicenseId: z.string().uuid().nullable().optional(),
  metadata: z.record(z.unknown()).nullable().optional()
});
var UpdateOrganizationLocationDtoSchema = CreateOrganizationLocationDtoSchema.partial();
var DbOrganizationBankAccountSchema = z.object({
  id: z.string().uuid(),
  organization_id: z.string().uuid(),
  achpc_account_token: z.string(),
  account_name: z.string().nullable(),
  account_type: AccountTypeSchema.nullable(),
  last_four: z.string().nullable(),
  is_default: z.boolean(),
  is_deleted: z.boolean(),
  created_at: z.union([z.string(), z.date()]),
  updated_at: z.union([z.string(), z.date()])
});
var CreateDbOrganizationBankAccountSchema = z.object({
  id: z.string().uuid().optional(),
  organization_id: z.string().uuid(),
  achpc_account_token: z.string(),
  account_name: z.string().nullable().optional(),
  account_type: AccountTypeSchema.nullable().optional(),
  last_four: z.string().nullable().optional(),
  is_default: z.boolean().optional(),
  is_deleted: z.boolean().optional(),
  created_at: z.string().optional(),
  updated_at: z.string().optional()
});
var OrganizationBankAccountSchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string().uuid(),
  achpcAccountToken: z.string(),
  accountName: z.string().nullable(),
  accountType: AccountTypeSchema.nullable(),
  lastFour: z.string().nullable(),
  isDefault: z.boolean(),
  isDeleted: z.boolean(),
  createdAt: z.date(),
  updatedAt: z.date()
});
var OrganizationBankAccountDtoSchema = z.object({
  id: z.string(),
  organizationId: z.string(),
  accountName: z.string().nullable(),
  accountType: AccountTypeSchema.nullable(),
  lastFour: z.string().nullable(),
  isDefault: z.boolean(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime()
});
var CreateOrganizationBankAccountDtoSchema = z.object({
  achpcAccountToken: z.string(),
  accountName: z.string().nullable().optional(),
  accountType: AccountTypeSchema.nullable().optional(),
  lastFour: z.string().nullable().optional(),
  isDefault: z.boolean().optional()
});
var DbOrgBrandSchema = z.object({
  id: z.string().uuid(),
  organization_id: z.string().uuid(),
  brand_id: z.string().uuid(),
  is_primary: z.boolean(),
  fulfillment_method: FulfillmentMethodSchema.nullable().default(null),
  spread_rate: z.string().nullable().default(null),
  created_at: z.union([z.string(), z.date()])
});
var OrgBrandSchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string().uuid(),
  brandId: z.string().uuid(),
  isPrimary: z.boolean(),
  fulfillmentMethod: FulfillmentMethodSchema.nullable().default(null),
  spreadRate: z.number().nullable().default(null),
  createdAt: z.date()
});
var OrgBrandDtoSchema = z.object({
  id: z.string(),
  organizationId: z.string(),
  brandId: z.string(),
  isPrimary: z.boolean(),
  fulfillmentMethod: FulfillmentMethodSchema.nullable().default(null),
  spreadRate: z.number().nullable().default(null),
  createdAt: z.string().datetime()
});
var OrgBrandWithDetailsDtoSchema = OrgBrandDtoSchema.extend({
  brandName: z.string(),
  brandLogo: z.string().nullable(),
  brandRealm: ProductRealmSchema.default("cannabis")
});
var OrgBrandFulfillmentInfoSchema = z.object({
  organizationId: z.string().uuid(),
  brandId: z.string().uuid(),
  brandRealm: ProductRealmSchema,
  fulfillmentMethod: FulfillmentMethodSchema.nullable(),
  spreadRate: z.number().nullable(),
  isPrimary: z.boolean()
});
var BrandBuyerSpreadOverrideSchema = z.object({
  id: z.string().uuid(),
  brandId: z.string().uuid(),
  buyerOrganizationId: z.string().uuid(),
  spreadRate: z.number(),
  reason: z.string().nullable(),
  createdAt: z.date(),
  updatedAt: z.date()
});
var DbMemberStockLocationSchema = z.object({
  id: z.string().uuid(),
  member_id: z.string().uuid(),
  stock_location_id: z.string().uuid(),
  can_view: z.boolean(),
  can_edit: z.boolean(),
  created_at: z.union([z.string(), z.date()])
});
var MemberStockLocationSchema = z.object({
  id: z.string().uuid(),
  memberId: z.string().uuid(),
  stockLocationId: z.string().uuid(),
  canView: z.boolean(),
  canEdit: z.boolean(),
  createdAt: z.date()
});
var MemberStockLocationDtoSchema = z.object({
  id: z.string(),
  memberId: z.string(),
  stockLocationId: z.string(),
  canView: z.boolean(),
  canEdit: z.boolean(),
  createdAt: z.string().datetime()
});
var UpdateMemberStockLocationAccessDtoSchema = z.object({
  canView: z.boolean(),
  canEdit: z.boolean()
});
var DbMemberBrandSchema = z.object({
  id: z.string().uuid(),
  member_id: z.string().uuid(),
  brand_id: z.string().uuid(),
  can_view: z.boolean(),
  can_edit: z.boolean(),
  created_at: z.union([z.string(), z.date()])
});
var MemberBrandSchema = z.object({
  id: z.string().uuid(),
  memberId: z.string().uuid(),
  brandId: z.string().uuid(),
  canView: z.boolean(),
  canEdit: z.boolean(),
  createdAt: z.date()
});
var MemberBrandDtoSchema = z.object({
  id: z.string(),
  memberId: z.string(),
  brandId: z.string(),
  canView: z.boolean(),
  canEdit: z.boolean(),
  createdAt: z.string().datetime()
});
var DbMemberComplianceLicenseSchema = z.object({
  id: z.string().uuid(),
  member_id: z.string().uuid(),
  compliance_license_id: z.string().uuid(),
  is_primary: z.boolean(),
  can_view: z.boolean(),
  can_edit: z.boolean(),
  created_at: z.union([z.string(), z.date()])
});
var MemberComplianceLicenseSchema = z.object({
  id: z.string().uuid(),
  memberId: z.string().uuid(),
  complianceLicenseId: z.string().uuid(),
  isPrimary: z.boolean(),
  canView: z.boolean(),
  canEdit: z.boolean(),
  createdAt: z.date()
});
var MemberComplianceLicenseDtoSchema = z.object({
  id: z.string(),
  memberId: z.string(),
  complianceLicenseId: z.string(),
  isPrimary: z.boolean(),
  canView: z.boolean(),
  canEdit: z.boolean(),
  createdAt: z.string().datetime()
});
var ACCESS_GRANT_RESOURCE_TYPES = ["stock_location", "driver", "license", "shipment", "route_plan"];
var AccessGrantResourceTypeSchema = z.enum(ACCESS_GRANT_RESOURCE_TYPES);
var ACCESS_GRANT_LEVELS = ["view", "operate", "manage"];
var AccessGrantLevelSchema = z.enum(ACCESS_GRANT_LEVELS);
var DbOrganizationAccessGrantSchema = z.object({
  id: z.string().uuid(),
  grantor_org_id: z.string().uuid(),
  grantee_org_id: z.string().uuid(),
  resource_type: AccessGrantResourceTypeSchema,
  access_level: AccessGrantLevelSchema,
  is_active: z.boolean(),
  granted_by: z.string().uuid().nullable(),
  expires_at: z.union([z.string(), z.date()]).nullable(),
  notes: z.string().nullable(),
  metadata: z.record(z.unknown()),
  created_at: z.union([z.string(), z.date()]),
  updated_at: z.union([z.string(), z.date()])
});
var OrganizationAccessGrantSchema = z.object({
  id: z.string().uuid(),
  grantorOrgId: z.string().uuid(),
  granteeOrgId: z.string().uuid(),
  resourceType: AccessGrantResourceTypeSchema,
  accessLevel: AccessGrantLevelSchema,
  isActive: z.boolean(),
  grantedBy: z.string().uuid().nullable(),
  expiresAt: z.date().nullable(),
  notes: z.string().nullable(),
  metadata: z.record(z.unknown()),
  createdAt: z.date(),
  updatedAt: z.date()
});
var OrganizationAccessGrantDtoSchema = z.object({
  id: z.string(),
  grantorOrgId: z.string(),
  granteeOrgId: z.string(),
  resourceType: AccessGrantResourceTypeSchema,
  accessLevel: AccessGrantLevelSchema,
  isActive: z.boolean(),
  grantedBy: z.string().nullable(),
  expiresAt: z.string().datetime().nullable(),
  notes: z.string().nullable(),
  metadata: z.record(z.unknown()),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime()
});
var METRC_INTEGRATION_STATUSES = ["pending_validation", "active", "invalid", "revoked"];
var MetrcIntegrationStatusSchema = z.enum(METRC_INTEGRATION_STATUSES);
var MetrcIntegrationFacilitySchema = z.object({
  licenseNumber: z.string(),
  facilityName: z.string().nullable(),
  displayName: z.string().nullable(),
  isOwner: z.boolean(),
  isManager: z.boolean()
});
var MetrcIntegrationSchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string().uuid(),
  memberId: z.string().uuid().nullable(),
  stateCode: z.string(),
  encryptedUserKey: z.custom(),
  encryptionIv: z.custom(),
  encryptionAuthTag: z.custom(),
  keyFingerprint: z.string(),
  keyVersion: z.number(),
  status: MetrcIntegrationStatusSchema,
  lastValidatedAt: z.date().nullable(),
  validationError: z.string().nullable(),
  accessibleFacilities: z.array(MetrcIntegrationFacilitySchema),
  configuredBy: z.string().uuid().nullable(),
  configuredAt: z.date().nullable(),
  metadata: z.record(z.unknown()),
  createdAt: z.date(),
  updatedAt: z.date()
});
var OrganizationWithMembershipDtoSchema = OrganizationDtoSchema.extend({
  membership: z.object({
    id: z.string(),
    role: MemberRoleSchema,
    joinedAt: z.string().datetime().nullable()
  })
});

export {
  ORGANIZATION_TYPES,
  OrganizationTypeSchema,
  MEMBER_ROLES,
  MemberRoleSchema,
  SCOPE_MODES,
  ScopeModeSchema,
  LOCATION_TYPES,
  LocationTypeSchema,
  FULFILLMENT_METHODS,
  FulfillmentMethodSchema,
  ACCOUNT_TYPES,
  AccountTypeSchema,
  DbOrganizationSchema,
  CreateDbOrganizationSchema,
  UpdateDbOrganizationSchema,
  OrganizationSchema,
  OrganizationDtoSchema,
  CreateOrganizationDtoSchema,
  UpdateOrganizationDtoSchema,
  DbOrganizationMemberSchema,
  CreateDbOrganizationMemberSchema,
  OrganizationMemberSchema,
  OrganizationMemberDtoSchema,
  AddMemberDtoSchema,
  UpdateMemberDtoSchema,
  UpdateMemberRoleDtoSchema,
  OnboardPortalRoleSchema,
  OnboardMemberDtoSchema,
  OnboardMemberResponseDtoSchema,
  DbOrganizationLocationSchema,
  CreateDbOrganizationLocationSchema,
  UpdateDbOrganizationLocationSchema,
  OrganizationLocationSchema,
  OrganizationLocationDtoSchema,
  CreateOrganizationLocationDtoSchema,
  UpdateOrganizationLocationDtoSchema,
  DbOrganizationBankAccountSchema,
  CreateDbOrganizationBankAccountSchema,
  OrganizationBankAccountSchema,
  OrganizationBankAccountDtoSchema,
  CreateOrganizationBankAccountDtoSchema,
  DbOrgBrandSchema,
  OrgBrandSchema,
  OrgBrandDtoSchema,
  OrgBrandWithDetailsDtoSchema,
  OrgBrandFulfillmentInfoSchema,
  BrandBuyerSpreadOverrideSchema,
  DbMemberStockLocationSchema,
  MemberStockLocationSchema,
  MemberStockLocationDtoSchema,
  UpdateMemberStockLocationAccessDtoSchema,
  DbMemberBrandSchema,
  MemberBrandSchema,
  MemberBrandDtoSchema,
  DbMemberComplianceLicenseSchema,
  MemberComplianceLicenseSchema,
  MemberComplianceLicenseDtoSchema,
  ACCESS_GRANT_RESOURCE_TYPES,
  AccessGrantResourceTypeSchema,
  ACCESS_GRANT_LEVELS,
  AccessGrantLevelSchema,
  DbOrganizationAccessGrantSchema,
  OrganizationAccessGrantSchema,
  OrganizationAccessGrantDtoSchema,
  METRC_INTEGRATION_STATUSES,
  MetrcIntegrationStatusSchema,
  MetrcIntegrationFacilitySchema,
  MetrcIntegrationSchema,
  OrganizationWithMembershipDtoSchema
};
